import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import { TabPill } from '@components/v2/ui/Tabs/components/TabPill';
import { TabsScrollArea } from '@components/v2/ui/Tabs/components/TabsScrollArea';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Stack } from '@mui/material';
import { ChevronDown, ChevronUp, CircleFadingPlus } from 'lucide-react';
import { useRef } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useCatalogSectionsDnd } from '../../hooks/useCatalogSectionsDnd';
import { ReorderSectionsParams } from '../../hooks/useCatalogSectionsDnd.types';
import {
  ADD_TRIGGER_SX,
  MORE_PILL_ACTIVE_MAX_WIDTH_PX,
  TAB_GAP_PX,
  TAB_SORT_TRANSITION,
} from './catalogSectionTabsBar.constants';
import {
  CatalogSectionTabsBarProps,
  CatalogSectionTabsTrigger,
} from './catalogSectionTabsBar.types';
import { useCatalogSectionTabsBar } from './useCatalogSectionTabsBar';

export const CatalogSectionTabsBar = ({
  sections,
  activeSectionId,
  canManage,
  isManagementOpen,
  onSelect,
  onOpenManagement,
  onReorder,
}: CatalogSectionTabsBarProps) => {
  const { translate } = useTranslation();
  const moreTriggerRef = useRef<HTMLDivElement>(null);
  const {
    isScrollable,
    containerRef,
    measureRef,
    visibleSections,
    activeHiddenSection,
    trigger,
    handleSelect,
  } = useCatalogSectionTabsBar({
    sections,
    activeSectionId,
    canManage,
    onSelect,
  });

  if (sections.length === 0) {
    return null;
  }

  const openManagement = () => onOpenManagement(moreTriggerRef.current);

  const pills = (
    <SectionPills
      sections={visibleSections}
      activeSectionId={activeSectionId}
      canManage={canManage}
      onSelect={handleSelect}
      onReorder={onReorder}
    />
  );

  if (isScrollable) {
    return (
      <TabsScrollArea gap={`${TAB_GAP_PX}px`} testId={CatalogProductsTestIds.SECTION_TABS}>
        {pills}
      </TabsScrollArea>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '100%', minWidth: 0, overflow: 'hidden' }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          display: 'flex',
          flexDirection: 'row',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 'max-content',
          gap: `${TAB_GAP_PX}px`,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {sections.map((section) => (
          <Box key={`measure-${section.id}`} sx={{ display: 'inline-flex' }}>
            <TabPill label={section.name} secondaryLabel={section.itemsCount} />
          </Box>
        ))}
      </Box>

      <Stack
        role="tablist"
        direction="row"
        test-id={CatalogProductsTestIds.SECTION_TABS}
        sx={{ alignItems: 'center', gap: `${TAB_GAP_PX}px` }}
      >
        {pills}
        <Box ref={moreTriggerRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {trigger === CatalogSectionTabsTrigger.PLUS && (
            <IconButton
              icon={CircleFadingPlus}
              color="primary"
              size="large"
              shape="round"
              isActive={isManagementOpen}
              ariaLabel={translate(LangEnum.CATALOGS_SECTIONS_MANAGE)}
              ariaExpanded={isManagementOpen}
              testId={CatalogProductsTestIds.SECTION_TABS_ADD_BUTTON}
              sx={ADD_TRIGGER_SX}
              onClick={openManagement}
            />
          )}
          {trigger === CatalogSectionTabsTrigger.MORE && (
            <TabPill
              label={activeHiddenSection?.name ?? LangEnum.GLOBAL_MORE}
              secondaryLabel={activeHiddenSection?.itemsCount}
              tooltip={activeHiddenSection?.name}
              maxWidth={activeHiddenSection ? MORE_PILL_ACTIVE_MAX_WIDTH_PX : undefined}
              isActive={isManagementOpen || activeHiddenSection !== null}
              endIcon={<Icon icon={isManagementOpen ? ChevronUp : ChevronDown} size="sm" />}
              testId={CatalogProductsTestIds.SECTION_TABS_MORE_BUTTON}
              onClick={openManagement}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
};

interface SectionPillsProps {
  sections: CatalogSectionTabsBarProps['sections'];
  activeSectionId: string;
  canManage: boolean;
  onSelect: (sectionId: string) => void;
  onReorder: (params: ReorderSectionsParams) => void;
}

const SectionPills = ({
  sections,
  activeSectionId,
  canManage,
  onSelect,
  onReorder,
}: SectionPillsProps) => {
  const orderedIds = sections.map((section) => section.id);
  const { sensors, activeId, handleDragStart, handleDragCancel, handleDragEnd } =
    useCatalogSectionsDnd({ orderedIds, onReorder });

  if (!canManage) {
    return (
      <>
        {sections.map((section) => (
          <SectionPill
            key={section.id}
            section={section}
            isActive={section.id === activeSectionId}
            onSelect={onSelect}
          />
        ))}
      </>
    );
  }

  const draggedSection = sections.find((section) => section.id === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
        {sections.map((section) => (
          <SortableSectionPill
            key={section.id}
            section={section}
            isActive={section.id === activeSectionId}
            onSelect={onSelect}
          />
        ))}
      </SortableContext>
      <DragOverlay style={{ cursor: 'grabbing' }}>
        {draggedSection && (
          <TabPill
            label={draggedSection.name}
            secondaryLabel={draggedSection.itemsCount}
            isActive={draggedSection.id === activeSectionId}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

interface SectionPillProps {
  section: CatalogSectionTabsBarProps['sections'][number];
  isActive: boolean;
  onSelect: (sectionId: string) => void;
}

const SortableSectionPill = ({ section, isActive, onSelect }: SectionPillProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    transition: TAB_SORT_TRANSITION,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={isDragging ? undefined : { transform: CSS.Translate.toString(transform), transition }}
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        touchAction: 'manipulation',
        opacity: isDragging ? 0 : 1,
      }}
    >
      <SectionPill section={section} isActive={isActive} onSelect={onSelect} />
    </Box>
  );
};

const SectionPill = ({ section, isActive, onSelect }: SectionPillProps) => {
  return (
    <TabPill
      label={section.name}
      secondaryLabel={section.itemsCount}
      isActive={isActive}
      testId={CatalogProductsTestIds.SECTION_TAB}
      onClick={() => onSelect(section.id)}
    />
  );
};
